import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  deleteUser,
  createUser,
  updateUser,
  resetUserState,
} from "../../features/userSlice";
import App from "../../Utils/LayoutMobile";

/* ─── Design tokens ─────────────────────────────────────── */
const C_PINK   = "#F04DA0";
const C_PURPLE = "#8B2FC9";
const C_DARK   = "#1a0040";
const C_GRAD   = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;

/* ─── Toast ─────────────────────────────────────────────── */
let _tid = 0;
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type = "success") => {
    const id = ++_tid;
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  };
  return { toasts, success: m => add(m, "success"), error: m => add(m, "error") };
}

/* ─── Avatar ─────────────────────────────────────────────── */
function Avatar({ src, name, size = 44 }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const colors = [[C_PINK, C_PURPLE], ["#0284c7", "#0f766e"], ["#7c3aed", "#ea580c"], ["#16a34a", "#0284c7"]];
  const idx = (name?.charCodeAt(0) || 0) % colors.length;
  if (src && !err) {
    return (
      <img src={src} alt={name} onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover",
          border: "2px solid #f0e8f8", flexShrink: 0 }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(135deg,${colors[idx][0]},${colors[idx][1]})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontSize: size * 0.33, fontWeight: 800,
      border: "2px solid rgba(255,255,255,0.3)" }}>
      {initials}
    </div>
  );
}

/* ─── Confirm Dialog ─────────────────────────────────────── */
function ConfirmDialog({ open, onConfirm, onCancel, name }) {
  if (!open) return null;
  return (
    <div className="ul-overlay" onClick={onCancel}>
      <div className="ul-dialog" onClick={e => e.stopPropagation()}>
        <div className="ul-dialog-icon">
          <svg width="22" height="22" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
            <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
          </svg>
        </div>
        <h3 className="ul-dialog-title">Delete Speaker Member?</h3>
        <p className="ul-dialog-sub"><strong>{name}</strong> will be permanently removed. This action cannot be undone.</p>
        <div className="ul-dialog-actions">
          <button className="ul-dialog-cancel" onClick={onCancel}>Cancel</button>
          <button className="ul-dialog-confirm" onClick={onConfirm}>Yes, Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Bio Expand Modal ───────────────────────────────────── */
function BioModal({ open, name, bio, onClose }) {
  if (!open) return null;
  return (
    <div className="ul-overlay" onClick={onClose}>
      <div className="ul-bio-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C_PINK, marginBottom: 4 }}>Speaker Bio</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C_DARK }}>{name}</div>
          </div>
          <button className="ul-modal-close" onClick={onClose}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div style={{
          background: "#faf5ff", border: "1px solid rgba(139,47,201,0.1)",
          borderRadius: 12, padding: "16px 18px",
          fontSize: 14, color: "#4a3a5a", lineHeight: 1.75,
          maxHeight: 300, overflowY: "auto",
        }}>
          {bio || <span style={{ color: "#c4b4d8", fontStyle: "italic" }}>No bio provided.</span>}
        </div>
      </div>
    </div>
  );
}

/* ─── Faculty Form Modal (Add + Edit) ───────────────────── */
function FacultyModal({ open, mode, editUser, onClose, onSubmit, loading }) {
  const fileRef = useRef(null);
  // ✅ bio added to initial form state
  const [form, setForm] = useState({ name: "", email: "", location: "", bio: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === "edit" && editUser) {
      setForm({
        name:     editUser.name     || "",
        email:    editUser.email    || "",
        location: editUser.location || "",
        bio:      editUser.bio      || "",   // ✅ populate bio on edit
      });
      setImagePreview(editUser.image || null);
      setImageFile(null);
    } else if (mode === "add") {
      setForm({ name: "", email: "", location: "", bio: "" });
      setImageFile(null);
      setImagePreview(null);
      setErrors({});
    }
  }, [mode, editUser, open]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Only image files allowed!"); return; }
    if (file.size / 1024 / 1024 > 3) { alert("Image must be less than 3MB!"); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

 const validate = () => {
  const e = {};
  if (!form.name.trim()) e.name = "Full name is required";
  // ✅ Email optional — फक्त काही टाकलं असेल तरच validate करा
  if (form.email.trim() && !/\S+@\S+\.\S+/.test(form.email)) {
    e.email = "Invalid email";
  }
  if (!form.location.trim()) e.location = "Location is required";
  if (mode === "add" && !imageFile) e.image = "Please upload an image";
  setErrors(e);
  return Object.keys(e).length === 0;
};

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const formData = new FormData();
    formData.append("name",     form.name);
    formData.append("email",    form.email);
    formData.append("location", form.location);
    formData.append("bio",      form.bio);        // ✅ send bio
    formData.append("role",     "faculty");
    if (imageFile) {
      formData.append("image", imageFile);
    } else if (mode === "edit" && imagePreview) {
      formData.append("imageUrl", imagePreview);
    }
    onSubmit(formData);
  };

  if (!open) return null;

  return (
    <div className="ul-overlay" onClick={onClose}>
      <div className="ul-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ul-modal-header">
          <div>
            <h2 className="ul-modal-title">
              {mode === "add" ? "Add Speaker Member" : "Edit Speaker Member"}
            </h2>
            <p className="ul-modal-sub">
              {mode === "add" ? "Fill in the details to add a new speaker member." : "Update the speaker member's information."}
            </p>
          </div>
          <button className="ul-modal-close" onClick={onClose}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ul-modal-form">

          {/* Image Upload */}
          <div className="ul-form-row" style={{ marginBottom: 24 }}>
            <div className="ul-image-upload-wrap">
              <div className="ul-image-preview" onClick={() => fileRef.current?.click()}>
                {imagePreview
                  ? <img src={imagePreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                  : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <svg width="22" height="22" fill="none" stroke={C_PINK} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span style={{ fontSize: 11, color: C_PINK, fontWeight: 700 }}>Upload Photo</span>
                    </div>
                  )
                }
              </div>
              <div>
                <button type="button" className="ul-upload-btn" onClick={() => fileRef.current?.click()}>
                  {imagePreview ? "Change Photo" : "Select Photo"}
                </button>
                <p style={{ fontSize: 11.5, color: "#9a86b8", marginTop: 6 }}>JPG, PNG, WEBP · Max 3MB</p>
                {errors.image && <p className="ul-field-error">{errors.image}</p>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageChange} />
            </div>
          </div>

          {/* Name + Email */}
          <div className="ul-form-grid">
            <div className="ul-field">
              <label className="ul-label">Full Name <span style={{ color: C_PINK }}>*</span></label>
              <input
                className={`ul-input ${errors.name ? "ul-input--error" : ""}`}
                type="text" placeholder="e.g. Dr. Priya Sharma"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              />
              {errors.name && <p className="ul-field-error">{errors.name}</p>}
            </div>
            <div className="ul-field">
              <label className="ul-label">Email<span style={{ color: "#c4b4d8", fontWeight: 500, marginLeft: 6, fontSize: 11 }}>(optional)</span></label>
              <input
                className={`ul-input ${errors.email ? "ul-input--error" : ""}`}
                type="email" placeholder="e.g. priya@example.com"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              />
              {errors.email && <p className="ul-field-error">{errors.email}</p>}
            </div>
          </div>

          {/* Location */}
          <div className="ul-field" style={{ marginTop: 16 }}>
            <label className="ul-label">Location <span style={{ color: C_PINK }}>*</span></label>
            <input
              className={`ul-input ${errors.location ? "ul-input--error" : ""}`}
              type="text" placeholder="e.g. Mumbai, India"
              value={form.location}
              onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
            />
            {errors.location && <p className="ul-field-error">{errors.location}</p>}
          </div>

          {/* ✅ Bio Textarea */}
          <div className="ul-field" style={{ marginTop: 16 }}>
            <label className="ul-label">
              Bio
              <span style={{ color: "#c4b4d8", fontWeight: 500, marginLeft: 6, fontSize: 11 }}>(optional)</span>
            </label>
            <textarea
              className="ul-textarea"
              placeholder="A short bio about the Speaker member — their expertise, experience, or achievements…"
              rows={4}
              value={form.bio}
              onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
            />
            <span style={{ fontSize: 11, color: "#c4b4d8", alignSelf: "flex-end" }}>
              {form.bio.length} / 500
            </span>
          </div>

          {/* Footer */}
          <div className="ul-modal-footer">
            <button type="button" className="ul-dialog-cancel" onClick={onClose} style={{ flex: "unset", padding: "11px 28px" }}>
              Cancel
            </button>
            <button type="submit" className="ul-add-btn" disabled={loading} style={{ padding: "11px 32px" }}>
              {loading
                ? <span className="ul-btn-spinner" style={{ borderTopColor: "#fff", border: "2px solid rgba(255,255,255,.3)", borderTopWidth: 2 }} />
                : (
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )
              }
              <span>{mode === "add" ? (loading ? "Adding…" : "Add Speaker") : (loading ? "Saving…" : "Save Changes")}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
const UserList = () => {
  const dispatch = useDispatch();
  const toast    = useToast();

  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState("");
  const [confirm, setConfirm]   = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [modalOpen, setModalOpen]     = useState(false);
  const [modalMode, setModalMode]     = useState("add");
  const [editingUser, setEditingUser] = useState(null);

  // ✅ Bio modal state
  const [bioModal, setBioModal] = useState({ open: false, name: "", bio: "" });

  const { users, loading, pagination, success, error } = useSelector(s => s.user);

  useEffect(() => {
    dispatch(fetchUsers({ role: "faculty", status: "false", page, limit: 100 }));
  }, [dispatch, page]);

  useEffect(() => {
    if (success) {
      toast.success(modalMode === "add" ? "Speaker member added!" : "Speaker member updated!");
      setModalOpen(false);
      setEditingUser(null);
      dispatch(resetUserState());
      dispatch(fetchUsers({ role: "faculty", status: "false", page, limit: 100 }));
    }
    if (error) {
      toast.error(error || "Something went wrong");
      dispatch(resetUserState());
    }
  }, [success, error]);

  const openAddModal  = () => { setModalMode("add");  setEditingUser(null); setModalOpen(true); };
  const openEditModal = (user) => { setModalMode("edit"); setEditingUser(user); setModalOpen(true); };

  const handleModalSubmit = (formData) => {
    if (modalMode === "add") dispatch(createUser(formData));
    else dispatch(updateUser({ formData, id: editingUser._id }));
  };

  const handleDelete = async () => {
    if (!confirm) return;
    setDeleting(confirm.id);
    setConfirm(null);
    try {
      await dispatch(deleteUser(confirm.id)).unwrap();
      toast.success("Speaker member deleted successfully");
      dispatch(fetchUsers({ role: "faculty", status: "false", page, limit: 100 }));
    } catch {
      toast.error("Failed to delete Speaker member");
    } finally {
      setDeleting(null);
    }
  };

  const filtered = (users || []).filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.location?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil((pagination?.totalDocuments || filtered.length) / (pagination?.limit || 100));

  return (
    <App>
      <div className="ul-page">

        {/* Toasts */}
        <div className="ul-toast-container">
          {toast.toasts.map(t => (
            <div key={t.id} className={`ul-toast ul-toast--${t.type}`}>
              {t.type === "success"
                ? <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              }
              <span>{t.msg}</span>
            </div>
          ))}
        </div>

        <ConfirmDialog open={!!confirm} name={confirm?.name} onConfirm={handleDelete} onCancel={() => setConfirm(null)} />

        <FacultyModal
          open={modalOpen} mode={modalMode} editUser={editingUser}
          onClose={() => { setModalOpen(false); dispatch(resetUserState()); }}
          onSubmit={handleModalSubmit} loading={loading}
        />

        {/* ✅ Bio Read Modal */}
        <BioModal
          open={bioModal.open}
          name={bioModal.name}
          bio={bioModal.bio}
          onClose={() => setBioModal({ open: false, name: "", bio: "" })}
        />

        {/* Page Header */}
        <div className="ul-page-header">
          <div>
            <div className="ul-eyebrow"><span className="ul-eyebrow-dot" />Admin Panel</div>
            <h1 className="ul-page-title">Our <span className="ul-grad">Speakers</span></h1>
            <p className="ul-page-sub">Manage faculty members who will speak at the conference.</p>
          </div>
          <button className="ul-add-btn" onClick={openAddModal}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Add Speaker</span>
          </button>
        </div>

        {/* Stats strip */}
        <div className="ul-stats-strip">
          {[
            { label: "Total Speakers", val: users?.length || 0,   icon: <svg width="16" height="16" fill="none" stroke={C_PINK}   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>, c: C_PINK,   bg: "#fff0f8", border: "#ffd0ea" },
            { label: "Showing",       val: filtered.length,       icon: <svg width="16" height="16" fill="none" stroke={C_PURPLE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>, c: C_PURPLE, bg: "#f5f0ff", border: "#e9d5ff" },
            { label: "Page",          val: `${page} / ${totalPages || 1}`, icon: <svg width="16" height="16" fill="none" stroke="#0284c7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="8 6 2 12 8 18"/><polyline points="16 6 22 12 16 18"/></svg>, c: "#0284c7", bg: "#f0f9ff", border: "#bae6fd" },
          ].map((s, i) => (
            <div key={i} className="ul-stat" style={{ "--sc": s.c, "--sbg": s.bg, "--sbo": s.border }}>
              <span className="ul-stat-icon">{s.icon}</span>
              <div>
                <div className="ul-stat-val">{s.val}</div>
                <div className="ul-stat-lbl">{s.label}</div>
              </div>
            </div>
          ))}

          <div className="ul-search-wrap">
            <svg className="ul-search-icon" width="15" height="15" fill="none" stroke={C_PINK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input className="ul-search" type="text" placeholder="Search by name or location…"
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && (
              <button className="ul-search-clear" onClick={() => setSearch("")}>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Table Card */}
        <div className="ul-card">
          {loading && (
            <div className="ul-skeleton-wrap">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="ul-skeleton-row" style={{ animationDelay: `${i * 0.07}s` }}>
                  <div className="ul-skel ul-skel--avatar" />
                  <div className="ul-skel ul-skel--name" />
                  <div className="ul-skel ul-skel--loc" />
                  <div className="ul-skel ul-skel--bio" />
                  <div className="ul-skel ul-skel--actions" />
                </div>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="ul-empty">
              <div className="ul-empty-icon">
                <svg width="32" height="32" fill="none" stroke={C_PINK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                </svg>
              </div>
              <h3 className="ul-empty-title">{search ? "No results found" : "No Speaker Members Yet"}</h3>
              <p className="ul-empty-sub">
                {search ? `No Speaker match "${search}". Try a different search.` : "Add your first speaker member to get started."}
              </p>
              {!search && (
                <button className="ul-add-btn ul-add-btn--sm" onClick={openAddModal}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add First Speaker
                </button>
              )}
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="ul-table-wrap">
              <table className="ul-table">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>#</th>
                    <th>Speaker</th>
                    <th>Location</th>
                    <th>Bio</th>          {/* ✅ new column */}
                    <th style={{ width: 180 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user, idx) => (
                    <tr key={user._id} className={deleting === user._id ? "ul-tr-deleting" : ""}
                      style={{ animationDelay: `${idx * 0.03}s` }}>
                      <td><span className="ul-row-num">{idx + 1}</span></td>
                      <td>
                        <div className="ul-user-cell">
                          <Avatar src={user.image} name={user.name} size={42} />
                          <div>
                            <div className="ul-user-name">{user.name || "—"}</div>
                            {user.email && <div className="ul-user-email">{user.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        {user.location ? (
                          <div className="ul-location-cell">
                            <svg width="12" height="12" fill="none" stroke={C_PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                            </svg>
                            {user.location}
                          </div>
                        ) : <span className="ul-muted">—</span>}
                      </td>

                      {/* ✅ Bio cell — shows truncated text + "Read more" button */}
                      <td>
                        {user.bio ? (
                          <div className="ul-bio-cell">
                            <span className="ul-bio-text">{user.bio}</span>
                            <button
                              className="ul-bio-read-btn"
                              onClick={() => setBioModal({ open: true, name: user.name, bio: user.bio })}
                            >
                              Read more
                            </button>
                          </div>
                        ) : (
                          <span className="ul-bio-empty">—</span>
                        )}
                      </td>

                      <td>
                        <div className="ul-actions">
                          <button className="ul-btn ul-btn--edit" onClick={() => openEditModal(user)} title="Edit">
                            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                            Edit
                          </button>
                          <button className="ul-btn ul-btn--delete"
                            onClick={() => setConfirm({ id: user._id, name: user.name })}
                            disabled={deleting === user._id}>
                            {deleting === user._id
                              ? <span className="ul-btn-spinner" />
                              : (
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                  <path d="M10 11v6M14 11v6"/>
                                  <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                </svg>
                              )
                            }
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="ul-pagination">
              <span className="ul-pag-info">
                Page {page} of {totalPages} · {pagination?.totalDocuments || filtered.length} members total
              </span>
              <div className="ul-pag-btns">
                <button className="ul-pag-btn" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  Prev
                </button>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const p = i + 1;
                  return (
                    <button key={p} className={`ul-pag-num ${page === p ? "ul-pag-num--active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  );
                })}
                <button className="ul-pag-btn" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                  Next
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes ul-fadeUp    { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes ul-pulse     { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes ul-spin      { to{transform:rotate(360deg)} }
        @keyframes ul-shimmer   { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes ul-toast-in  { from{opacity:0;transform:translateX(32px)} to{opacity:1;transform:translateX(0)} }
        @keyframes ul-row-in    { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
        @keyframes ul-dialog-in { from{opacity:0;transform:scale(.92) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes ul-modal-in  { from{opacity:0;transform:scale(.94) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }

        *,*::before,*::after{box-sizing:border-box}
        .ul-page{background:#f4eefa;min-height:100vh;padding:28px 32px 72px;display:flex;flex-direction:column;gap:24px;font-family:inherit;}
        .ul-page-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;animation:ul-fadeUp .4s ease both;}
        .ul-eyebrow{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:${C_PINK};background:rgba(240,77,160,.08);border:1px solid rgba(240,77,160,.2);padding:5px 14px 5px 10px;border-radius:50px;margin-bottom:12px;}
        .ul-eyebrow-dot{width:6px;height:6px;border-radius:50%;background:${C_PINK};animation:ul-pulse 2s infinite;flex-shrink:0;}
        .ul-page-title{font-size:clamp(22px,2.5vw,30px);font-weight:800;color:${C_DARK};letter-spacing:-.025em;margin:0 0 6px;}
        .ul-grad{background:linear-gradient(90deg,${C_PINK},#c066f0);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .ul-page-sub{font-size:13.5px;color:#8a7a9a;margin:0;}

        .ul-add-btn{display:inline-flex;align-items:center;gap:9px;padding:12px 26px;background:${C_GRAD};color:#fff;font-size:13.5px;font-weight:700;letter-spacing:.02em;border:none;border-radius:12px;cursor:pointer;box-shadow:0 8px 24px rgba(240,77,160,.3);transition:transform .25s cubic-bezier(.16,1,.3,1),box-shadow .25s;position:relative;overflow:hidden;font-family:inherit;white-space:nowrap;flex-shrink:0;}
        .ul-add-btn::before{content:"";position:absolute;inset:0;background:linear-gradient(135deg,${C_PURPLE},${C_PINK});opacity:0;transition:opacity .3s;}
        .ul-add-btn:hover{transform:translateY(-2px);box-shadow:0 14px 32px rgba(240,77,160,.42);}
        .ul-add-btn:hover::before{opacity:1;}
        .ul-add-btn span,.ul-add-btn svg{position:relative;z-index:1;}
        .ul-add-btn--sm{padding:10px 20px;font-size:13px;margin-top:4px;}
        .ul-add-btn:disabled{opacity:.7;cursor:not-allowed;transform:none;}

        .ul-stats-strip{display:flex;align-items:center;gap:12px;flex-wrap:wrap;animation:ul-fadeUp .4s ease .05s both;}
        .ul-stat{display:flex;align-items:center;gap:11px;background:#fff;border:1px solid var(--sbo);border-radius:14px;padding:12px 18px;box-shadow:0 2px 10px rgba(26,0,64,.04);}
        .ul-stat-icon{width:36px;height:36px;border-radius:9px;background:var(--sbg);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .ul-stat-val{font-size:18px;font-weight:800;color:${C_DARK};line-height:1;}
        .ul-stat-lbl{font-size:10.5px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.06em;margin-top:2px;}

        .ul-search-wrap{flex:1;min-width:220px;position:relative;margin-left:auto;}
        .ul-search-icon{position:absolute;left:14px;top:50%;transform:translateY(-50%);pointer-events:none;}
        .ul-search{width:100%;height:46px;border:1.5px solid #e8d8f8;border-radius:12px;padding:0 40px 0 40px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;transition:border-color .2s,box-shadow .2s;}
        .ul-search:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,.12);background:#fff;}
        .ul-search::placeholder{color:#c4b4d8;}
        .ul-search-clear{position:absolute;right:12px;top:50%;transform:translateY(-50%);width:22px;height:22px;border-radius:6px;border:none;background:#f0e8f8;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#9a86b8;transition:background .2s;}
        .ul-search-clear:hover{background:#e8d8f8;color:${C_PINK};}

        .ul-card{background:#fff;border-radius:20px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);overflow:hidden;animation:ul-fadeUp .4s ease .1s both;}

        .ul-skeleton-wrap{padding:8px 0;}
        .ul-skeleton-row{display:flex;align-items:center;gap:20px;padding:18px 24px;border-top:1px solid #f5f0fa;animation:ul-fadeUp .3s ease both;}
        .ul-skeleton-row:first-child{border-top:none;}
        .ul-skel{border-radius:8px;background:linear-gradient(90deg,#f5f0fa 25%,#ede5f5 50%,#f5f0fa 75%);background-size:400px 100%;animation:ul-shimmer 1.4s ease infinite;}
        .ul-skel--avatar{width:42px;height:42px;border-radius:50%;flex-shrink:0;}
        .ul-skel--name{width:160px;height:14px;}
        .ul-skel--loc{width:100px;height:14px;}
        .ul-skel--bio{width:180px;height:14px;}
        .ul-skel--actions{width:140px;height:32px;}

        .ul-empty{padding:64px 32px;display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;}
        .ul-empty-icon{width:72px;height:72px;border-radius:18px;background:rgba(240,77,160,.08);border:1px solid rgba(240,77,160,.15);display:flex;align-items:center;justify-content:center;margin-bottom:8px;}
        .ul-empty-title{font-size:18px;font-weight:800;color:${C_DARK};margin:0;}
        .ul-empty-sub{font-size:13.5px;color:#9a86b8;margin:0;max-width:340px;}

        .ul-table-wrap{overflow-x:auto;}
        .ul-table{width:100%;border-collapse:collapse;font-size:13px;}
        .ul-table thead tr{background:#faf5ff;}
        .ul-table th{padding:13px 20px;text-align:left;font-size:10.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#8a70a8;white-space:nowrap;border-bottom:2px solid #f0e8f8;}
        .ul-table tbody tr{border-top:1px solid #f5f0fa;transition:background .15s;animation:ul-row-in .3s ease both;}
        .ul-table tbody tr:hover td{background:#fdf8ff;}
        .ul-table tbody tr:first-child{border-top:none;}
        .ul-table td{padding:14px 20px;vertical-align:middle;}
        .ul-tr-deleting{opacity:.45;pointer-events:none;}

        .ul-row-num{display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:7px;background:#f5f0fa;color:#9a86b8;font-size:11px;font-weight:800;}
        .ul-user-cell{display:flex;align-items:center;gap:13px;}
        .ul-user-name{font-size:13.5px;font-weight:700;color:${C_DARK};}
        .ul-user-email{font-size:11.5px;color:#9a86b8;margin-top:2px;}
        .ul-location-cell{display:inline-flex;align-items:center;gap:5px;font-size:13px;color:#6a5a7a;font-weight:500;}
        .ul-muted{color:#c4b4d8;}

        /* ✅ Bio cell styles */
        .ul-bio-cell{display:flex;flex-direction:column;gap:4px;max-width:220px;}
        .ul-bio-text{font-size:12.5px;color:#6a5a7a;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
        .ul-bio-read-btn{align-self:flex-start;background:none;border:none;padding:0;font-size:11.5px;font-weight:700;color:${C_PURPLE};cursor:pointer;letter-spacing:.02em;transition:color .2s;font-family:inherit;}
        .ul-bio-read-btn:hover{color:${C_PINK};}
        .ul-bio-empty{color:#c4b4d8;font-size:13px;}

        .ul-actions{display:flex;align-items:center;gap:8px;}
        .ul-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:9px;font-size:12px;font-weight:700;border:none;cursor:pointer;transition:transform .2s,box-shadow .2s,background .2s;font-family:inherit;white-space:nowrap;}
        .ul-btn:disabled{opacity:.55;cursor:not-allowed;}
        .ul-btn--edit{background:rgba(139,47,201,.08);color:${C_PURPLE};border:1px solid rgba(139,47,201,.2);}
        .ul-btn--edit:hover:not(:disabled){background:${C_PURPLE};color:#fff;transform:translateY(-1px);box-shadow:0 6px 16px rgba(139,47,201,.3);}
        .ul-btn--delete{background:rgba(220,38,38,.06);color:#dc2626;border:1px solid rgba(220,38,38,.15);}
        .ul-btn--delete:hover:not(:disabled){background:#dc2626;color:#fff;transform:translateY(-1px);box-shadow:0 6px 16px rgba(220,38,38,.25);}
        .ul-btn-spinner{width:12px;height:12px;border-radius:50%;border:2px solid rgba(220,38,38,.25);border-top-color:#dc2626;animation:ul-spin .7s linear infinite;}

        .ul-pagination{display:flex;align-items:center;justify-content:space-between;padding:16px 22px;border-top:1px solid #f3ecfa;flex-wrap:wrap;gap:12px;}
        .ul-pag-info{font-size:12.5px;color:#9a86b8;font-weight:500;}
        .ul-pag-btns{display:flex;align-items:center;gap:6px;}
        .ul-pag-btn{display:inline-flex;align-items:center;gap:5px;padding:7px 14px;border-radius:9px;border:1.5px solid #e8d8f8;background:#fdfbff;font-size:12.5px;font-weight:700;color:#5a5070;cursor:pointer;transition:border-color .2s,background .2s;font-family:inherit;}
        .ul-pag-btn:hover:not(:disabled){border-color:${C_PINK};color:${C_PINK};background:#fff0f8;}
        .ul-pag-btn:disabled{opacity:.45;cursor:not-allowed;}
        .ul-pag-num{width:34px;height:34px;border-radius:9px;border:1.5px solid #e8d8f8;background:#fdfbff;font-size:13px;font-weight:700;color:#5a5070;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;font-family:inherit;}
        .ul-pag-num:hover{border-color:${C_PINK};color:${C_PINK};background:#fff0f8;}
        .ul-pag-num--active{background:${C_GRAD};border-color:transparent;color:#fff;box-shadow:0 4px 12px rgba(240,77,160,.3);}

        .ul-overlay{position:fixed;inset:0;z-index:9998;background:rgba(26,0,64,.45);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px;}

        .ul-dialog{background:#fff;border-radius:22px;border:1px solid #ede5f5;box-shadow:0 24px 64px rgba(26,0,64,.22);padding:36px 32px;max-width:400px;width:100%;display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;animation:ul-dialog-in .3s cubic-bezier(.16,1,.3,1) both;}
        .ul-dialog-icon{width:60px;height:60px;border-radius:16px;background:#fef2f2;border:1px solid #fecaca;display:flex;align-items:center;justify-content:center;margin-bottom:4px;}
        .ul-dialog-title{font-size:18px;font-weight:800;color:${C_DARK};margin:0;}
        .ul-dialog-sub{font-size:13.5px;color:#6a5a7a;margin:0;line-height:1.6;}
        .ul-dialog-sub strong{color:${C_DARK};}
        .ul-dialog-actions{display:flex;gap:10px;width:100%;margin-top:8px;}
        .ul-dialog-cancel{flex:1;height:44px;border-radius:11px;border:1.5px solid #e8d8f8;background:#fdfbff;font-size:13.5px;font-weight:700;color:#5a5070;cursor:pointer;transition:background .2s,border-color .2s;font-family:inherit;}
        .ul-dialog-cancel:hover{background:#f0e8f8;border-color:#d8c8f0;}
        .ul-dialog-confirm{flex:1;height:44px;border-radius:11px;border:none;background:#dc2626;font-size:13.5px;font-weight:700;color:#fff;cursor:pointer;transition:background .2s,transform .2s,box-shadow .2s;font-family:inherit;box-shadow:0 6px 18px rgba(220,38,38,.25);}
        .ul-dialog-confirm:hover{background:#b91c1c;transform:translateY(-1px);box-shadow:0 10px 24px rgba(220,38,38,.35);}

        /* Faculty modal */
        .ul-modal{background:#fff;border-radius:24px;border:1px solid #ede5f5;box-shadow:0 32px 80px rgba(26,0,64,.24);width:100%;max-width:560px;max-height:90vh;overflow-y:auto;animation:ul-modal-in .3s cubic-bezier(.16,1,.3,1) both;}
        .ul-modal-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:28px 28px 0;}
        .ul-modal-title{font-size:20px;font-weight:800;color:${C_DARK};margin:0 0 6px;}
        .ul-modal-sub{font-size:13px;color:#9a86b8;margin:0;}
        .ul-modal-close{width:36px;height:36px;border-radius:10px;border:1.5px solid #e8d8f8;background:#fdfbff;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#9a86b8;flex-shrink:0;transition:background .2s,color .2s;}
        .ul-modal-close:hover{background:#f0e8f8;color:${C_PINK};}
        .ul-modal-form{padding:24px 28px 28px;}
        .ul-modal-footer{display:flex;align-items:center;justify-content:flex-end;gap:10px;margin-top:28px;padding-top:20px;border-top:1px solid #f0e8f8;}

        /* Bio read modal */
        .ul-bio-modal{background:#fff;border-radius:20px;border:1px solid #ede5f5;box-shadow:0 24px 64px rgba(26,0,64,.2);width:100%;max-width:480px;padding:28px;animation:ul-modal-in .3s cubic-bezier(.16,1,.3,1) both;}

        /* Image upload */
        .ul-image-upload-wrap{display:flex;align-items:center;gap:20px;}
        .ul-image-preview{width:80px;height:80px;border-radius:50%;border:2px dashed #e8d8f8;background:#faf5ff;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;overflow:hidden;transition:border-color .2s;}
        .ul-image-preview:hover{border-color:${C_PINK};}
        .ul-upload-btn{display:inline-flex;align-items:center;gap:7px;padding:9px 18px;background:rgba(240,77,160,.08);color:${C_PINK};border:1.5px solid rgba(240,77,160,.25);border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;transition:background .2s,border-color .2s;}
        .ul-upload-btn:hover{background:rgba(240,77,160,.14);border-color:${C_PINK};}

        /* Form fields */
        .ul-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;}
        .ul-field{display:flex;flex-direction:column;gap:6px;}
        .ul-label{font-size:12.5px;font-weight:700;color:#5a5070;letter-spacing:.04em;}
        .ul-input{height:44px;border:1.5px solid #e8d8f8;border-radius:11px;padding:0 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;transition:border-color .2s,box-shadow .2s;}
        .ul-input:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,.12);background:#fff;}
        .ul-input::placeholder{color:#c4b4d8;}
        .ul-input--error{border-color:#fca5a5 !important;background:#fff9f9;}
        .ul-field-error{font-size:11.5px;color:#dc2626;margin:0;font-weight:600;}

        /* ✅ Textarea */
        .ul-textarea{border:1.5px solid #e8d8f8;border-radius:11px;padding:12px 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;resize:vertical;transition:border-color .2s,box-shadow .2s;width:100%;min-height:96px;}
        .ul-textarea:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,.12);background:#fff;}
        .ul-textarea::placeholder{color:#c4b4d8;}

        /* Toasts */
        .ul-toast-container{position:fixed;top:20px;right:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none;}
        .ul-toast{display:inline-flex;align-items:center;gap:9px;padding:12px 18px;border-radius:12px;font-size:13px;font-weight:600;box-shadow:0 8px 24px rgba(26,0,64,.15);animation:ul-toast-in .35s cubic-bezier(.16,1,.3,1) both;max-width:320px;}
        .ul-toast--success{background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0;}
        .ul-toast--error{background:#fef2f2;color:#dc2626;border:1px solid #fecaca;}

        @media(max-width:1024px){.ul-page{padding:22px 24px 60px;}}
        @media(max-width:768px){
          .ul-page{padding:16px 16px 52px;gap:16px;}
          .ul-page-header{flex-direction:column;align-items:stretch;}
          .ul-add-btn{justify-content:center;}
          .ul-stats-strip{flex-direction:column;align-items:stretch;}
          .ul-search-wrap{min-width:unset;margin-left:0;}
          .ul-stat{justify-content:flex-start;}
          .ul-table th:nth-child(1),.ul-table td:nth-child(1){display:none;}
          .ul-actions{flex-direction:column;}
          .ul-btn{justify-content:center;}
          .ul-pagination{flex-direction:column;align-items:center;}
          .ul-form-grid{grid-template-columns:1fr;}
          .ul-modal,.ul-bio-modal{border-radius:16px;}
        }
      `}</style>
    </App>
  );
};

export default UserList;
